/**
 * ERPProductSearch
 *
 * Busca de produtos para ERP com fallback IndexedDB (offline-safe).
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Package, X, ArrowRight, Star } from 'lucide-react';
import QrCodeScannerButton from '../QrCodeScanner/QrCodeScannerButton';
import { productService } from '../../../services/erpApi';
import { useErpProductCache } from '../../../hooks/useErpProductCache';
import { isBrowserOnline } from '../../../services/presaleDraftDb';
import './ERPProductSearch.css';

interface Product {
  id: number;
  name: string;
  sku?: string;
  barcode?: string;
  description?: string;
  price?: number;
  image_url?: string;
  brand?: string;
  featured?: boolean;
  stock_quantity?: number;
  stock_reserved?: number;
  stock_available?: number;
  active?: boolean;
}

type StockVariant = 'available' | 'reserved' | 'unavailable';

interface ProductStockInfo {
  label: string;
  variant: StockVariant;
}

const getProductStockInfo = (product: Product, offline: boolean): ProductStockInfo => {
  const quantity = Number(product.stock_quantity ?? 0) || 0;
  const reserved = Number(product.stock_reserved ?? 0) || 0;
  const available =
    product.stock_available != null
      ? Number(product.stock_available) || 0
      : Math.max(0, quantity - reserved);

  if (available > 0) {
    return {
      label: offline
        ? `Disp. (cache): ${Math.floor(available)}`
        : `Disponível: ${Math.floor(available)}`,
      variant: 'available',
    };
  }

  if (quantity > 0) {
    return {
      label: offline ? 'Sem disponível (cache)' : 'Sem disponível',
      variant: 'reserved',
    };
  }

  return {
    label: offline ? 'Sem estoque (cache)' : 'Sem estoque',
    variant: 'unavailable',
  };
};

interface ERPProductSearchProps {
  onSelect: (product: Product) => void;
  placeholder?: string;
  className?: string;
  showCamera?: boolean;
}

const ERPProductSearch: React.FC<ERPProductSearchProps> = ({
  onSelect,
  placeholder = 'Buscar por nome ou SKU do equipamento...',
  className = '',
  showCamera = false,
}) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fromCacheOnly, setFromCacheOnly] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { getProducts, searchProductsLocal, isOffline } = useErpProductCache();

  useEffect(() => {
    getProducts().catch(() => {});
  }, [getProducts]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const search = async () => {
      if (term.length < 2) {
        setResults([]);
        setIsOpen(false);
        setFromCacheOnly(false);
        return;
      }

      setLoading(true);
      setIsOpen(true);

      const offline = !isBrowserOnline() || isOffline;
      const searchLower = term.toLowerCase();

      const filterList = (list: Product[]) =>
        (list || [])
          .filter((p) => p.active !== false)
          .filter((p) => {
            return (
              p.name?.toLowerCase().includes(searchLower) ||
              p.sku?.toLowerCase().includes(searchLower) ||
              p.barcode?.toLowerCase().includes(searchLower) ||
              p.description?.toLowerCase().includes(searchLower)
            );
          })
          .slice(0, 6);

      try {
        await getProducts();
        const localResults = filterList(searchProductsLocal(term) as Product[]);

        if (offline) {
          setResults(localResults);
          setFromCacheOnly(true);
          return;
        }

        try {
          const response = await productService.getAll({
            search: term,
            active: true,
            limit: 6,
          });

          if (response.success) {
            const filtered = filterList(response.data || []);
            setResults(filtered.length > 0 ? filtered : localResults);
            setFromCacheOnly(filtered.length === 0 && localResults.length > 0);
          } else {
            setResults(localResults);
            setFromCacheOnly(localResults.length > 0);
          }
        } catch (apiError) {
          console.warn('[ERPProductSearch] API indisponível, usando cache:', apiError);
          setResults(localResults);
          setFromCacheOnly(true);
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        setResults([]);
        setFromCacheOnly(false);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [term, getProducts, searchProductsLocal, isOffline]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleSelect = (product: Product) => {
    onSelect(product);
    setIsOpen(false);
    setTerm('');
  };

  const handleClear = () => {
    setTerm('');
    setResults([]);
    setIsOpen(false);
    setFromCacheOnly(false);
  };

  const handleScan = async (raw: string) => {
    const code = String(raw || '').trim();
    if (!code) return;
    setTerm(code);
    try {
      await getProducts();
      const local = searchProductsLocal(code) as Product[];
      const exact = local.find((product) => {
        const barcode = String(product.barcode || '').trim();
        const sku = String(product.sku || '').trim();
        return barcode === code || sku === code;
      });
      if (exact) {
        handleSelect(exact);
        return;
      }
    } catch (error) {
      console.warn('[ERPProductSearch] leitura de código sem correspondência exata:', error);
    }
    setIsOpen(true);
  };

  const offlineMode = !isBrowserOnline() || isOffline || fromCacheOnly;

  return (
    <div className={`erp-product-search ${className}`} ref={containerRef}>
      <div className="erp-product-search-input-wrapper">
        <div className="erp-product-search-icon-left">
          {loading ? (
            <Loader2 size={20} className="erp-product-search-loader" />
          ) : (
            <Search
              size={20}
              className={`erp-product-search-icon ${isOpen ? 'erp-product-search-icon-active' : ''}`}
            />
          )}
        </div>

        <input
          type="text"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (term.length >= 2) {
              setIsOpen(true);
            }
          }}
          className={`erp-product-search-input ${isOpen ? 'erp-product-search-input-active' : ''} ${showCamera ? 'erp-product-search-input--camera' : ''} ${showCamera && term ? 'erp-product-search-input--camera-filled' : ''}`}
          placeholder={placeholder}
        />

        {showCamera && (
          <QrCodeScannerButton
            className={`erp-product-search-camera ${term ? 'erp-product-search-camera--shifted' : ''}`}
            title="Ler QR Code ou código de barras"
            onScan={handleScan}
          />
        )}

        {term && (
          <div className="erp-product-search-icon-right">
            <button onClick={handleClear} className="erp-product-search-clear-btn" type="button">
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {isOpen && term.length >= 2 && (
        <div className="erp-product-search-dropdown">
          <div className="erp-product-search-dropdown-header">
            <span className="erp-product-search-dropdown-title">
              {offlineMode ? 'Sugestões (cache local)' : 'Sugestões Medik'}
            </span>
            <span className="erp-product-search-dropdown-count">
              {results.length} Itens encontrados
            </span>
          </div>

          {offlineMode && (
            <div className="erp-product-search-cache-hint">
              Estoque sujeito a confirmação ao sincronizar
            </div>
          )}

          <div className="erp-product-search-dropdown-results">
            {results.length > 0 ? (
              results.map((product) => {
                const stockInfo = getProductStockInfo(product, offlineMode);

                return (
                  <button
                    key={product.id}
                    onClick={() => handleSelect(product)}
                    className="erp-product-search-option"
                    type="button"
                  >
                    <div className="erp-product-search-option-image">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} />
                      ) : (
                        <Package size={20} />
                      )}
                    </div>

                    <div className="erp-product-search-option-info">
                      <div className="erp-product-search-option-header">
                        {product.sku && (
                          <span className="erp-product-search-option-sku">{product.sku}</span>
                        )}
                        {product.featured && (
                          <Star size={10} className="erp-product-search-option-star" />
                        )}
                      </div>
                      <p className="erp-product-search-option-name">{product.name}</p>
                      {product.brand && (
                        <p className="erp-product-search-option-brand">{product.brand}</p>
                      )}
                    </div>

                    <div className="erp-product-search-option-price">
                      {product.price != null && (
                        <p className="erp-product-search-option-price-value">
                          {formatCurrency(product.price)}
                        </p>
                      )}
                      <div
                        className={`erp-product-search-option-stock erp-product-search-option-stock--${stockInfo.variant}`}
                      >
                        <div className="erp-product-search-option-stock-indicator" />
                        <span className="erp-product-search-option-stock-text">
                          {stockInfo.label}
                        </span>
                      </div>
                    </div>

                    <div className="erp-product-search-option-arrow">
                      <ArrowRight size={20} />
                    </div>
                  </button>
                );
              })
            ) : (
              !loading && (
                <div className="erp-product-search-empty">
                  <div className="erp-product-search-empty-icon">
                    <Package size={32} />
                  </div>
                  <p className="erp-product-search-empty-text">Nenhum produto compatível</p>
                </div>
              )
            )}
          </div>

          <div className="erp-product-search-dropdown-footer">
            <p className="erp-product-search-dropdown-footer-text">
              Utilize setas ↑↓ para navegar e Enter para selecionar
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPProductSearch;

export type { Product, ERPProductSearchProps };
